import { Progress } from '@heroui/react'
import { DateTime } from 'luxon'
import { useMemo, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { useAppStore, isApplicationLocked } from '@/store'
import SidebarControls from '@/components/account/SidebarControls'
import { createBackgroundSchema, requiresVideoIntro } from '@leangap/types'

import { StepItem } from './sidebar/StepItem'
import type { StepStatusType } from './sidebar/utils'
import { StepStatus } from './sidebar/utils'

const steps = [
	{ key: 'general', label: 'General Information', path: '/account/steps/general' },
	{ key: 'background', label: 'Your Background', path: '/account/steps/background' },
	{ key: 'program', label: 'Program Choice', path: '/account/steps/program' },
	{ key: 'aid', label: 'Financial Aid', path: '/account/steps/aid' },
] as const

export default function Sidebar() {
	const { user, lastTimeSaved } = useAppStore()
	const location = useLocation()

	// Scroll to top when navigating via the sidebar (route change)
	useEffect(() => {
		try {
			window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
		} catch (err) {
			// noop in environments without window
			void err
		}
	}, [location.pathname])

	// navigation handlers moved into SidebarControls

	// Extract only the fields needed for status computation to minimize re-renders
	// This prevents recomputation on every keystroke - only when these specific values change
	const country = user?.country
	const storedStatus = user?.application_status

	// Extract background fields individually for precise dependency tracking
	// The useMemo will only recompute when these specific fields change
	const bgFields = useMemo(
		() => ({
			share_proud: user?.share_proud ?? null,
			share_reason_to_apply: user?.share_reason_to_apply ?? null,
			video_intro: user?.video_intro ?? null,
			video_intro_is_public: user?.video_intro_is_public ?? null,
			share_what_want_to_learn: user?.share_what_want_to_learn ?? null,
			share_practical_skills: user?.share_practical_skills ?? null,
			share_extracurricular_activities: user?.share_extracurricular_activities ?? null,
			share_entrepreneurial_type: user?.share_entrepreneurial_type ?? null,
			share_what_makes_teenager: user?.share_what_makes_teenager ?? null,
			share_how_hear_about_us: user?.share_how_hear_about_us ?? null,
			share_how_hear_about_us_extra: user?.share_how_hear_about_us_extra ?? null,
			share_something_to_know_about: user?.share_something_to_know_about ?? null,
		}),
		[
			user?.share_proud,
			user?.share_reason_to_apply,
			user?.video_intro,
			user?.video_intro_is_public,
			user?.share_what_want_to_learn,
			user?.share_practical_skills,
			user?.share_extracurricular_activities,
			user?.share_entrepreneurial_type,
			user?.share_what_makes_teenager,
			user?.share_how_hear_about_us,
			user?.share_how_hear_about_us_extra,
			user?.share_something_to_know_about,
		]
	)

	// Derive step completion status efficiently:
	// - For general, program, aid: use stored application_status (updated by useStepStatusSync in each form)
	// - For background: recompute when country OR background fields change (handles cross-section dependency)
	//
	// This approach ensures:
	// 1. Changes to General fields (except country) don't trigger background recomputation
	// 2. Changes to Program/Aid fields don't trigger background recomputation
	// 3. Country changes immediately update background status (cross-section dependency)
	// 4. Background field changes update background status (same-section validation)
	const computedStatus = useMemo(() => {
		const hasStoredStatus =
			storedStatus &&
			typeof storedStatus === 'object' &&
			['general', 'background', 'program', 'aid'].every((k) => k in storedStatus)

		if (hasStoredStatus) {
			// Recompute background section to handle country-dependent video_intro requirement
			// The stored status may be stale if country changed while not on the Background page
			const needsVideoIntro = requiresVideoIntro(country)
			const backgroundSchema = createBackgroundSchema(needsVideoIntro)
			const backgroundValid = backgroundSchema.safeParse(bgFields).success

			return {
				general: storedStatus.general,
				background: backgroundValid,
				program: storedStatus.program,
				aid: storedStatus.aid,
			}
		}

		// Fallback: no stored status yet, compute all sections
		// This only happens on first load before any form has been visited
		return { general: false, background: false, program: false, aid: false }
	}, [storedStatus, country, bgFields])

	// Debug: log computedStatus for all steps on every render
	// console.log('[Sidebar] computedStatus:', computedStatus)

	// Only count the four main steps for progress (memoized)
	const { completedCount } = useMemo(() => {
		const stepKeys = steps.map((s) => s.key)
		const completed = stepKeys.filter((key) => Boolean(computedStatus[key])).length
		return { completedCount: completed }
	}, [computedStatus])

	const isSubmitted = isApplicationLocked(user?.application_process)

	// SubmitButton moved into SidebarControls

	return (
		<aside className="rounded-large bg-content1 shadow-small bg-background relative w-full px-4 py-3 lg:sticky lg:top-20 lg:z-10 lg:mb-8 lg:w-80 lg:p-0 lg:px-6 lg:pt-5 lg:pb-4">
			<header className="mb-5 hidden lg:block">
				<h2 className="mb-2 text-xl font-medium">Your Application</h2>
				<p className="text-small text-default-500">
					Complete each step of the application. Each step can be done separately.
				</p>
			</header>

			<Progress
				aria-label="Application progress"
				label="Steps"
				valueLabel={`${completedCount} of ${steps.length}`}
				maxValue={steps.length}
				showValueLabel={true}
				size="md"
				value={completedCount}
				minValue={0}
				classNames={{
					base: 'px-0.5',
					label: 'text-small',
					value: 'text-small text-default-400',
					indicator: 'bg-[var(--leangap-green)]',
				}}
			/>

			<ul className="mt-3 flex w-full max-w-full flex-col gap-y-2 px-2 py-0 lg:gap-y-3 lg:py-0">
				{steps.map((s, i) => {
					const done = Boolean(computedStatus[s.key])
					const active = location.pathname === s.path
					let status: StepStatusType = StepStatus.INCOMPLETE
					if (done) status = StepStatus.COMPLETE
					else if (active) status = StepStatus.ACTIVE
					// Optionally, show IN PROGRESS if not complete and has some data (custom logic can be added here)
					return (
						<StepItem key={s.key} step={s} index={i} status={status} active={active} to={s.path} />
					)
				})}
			</ul>

			<div className="bg-default-200 my-4 h-px lg:my-6" />

			<div className="hidden lg:block">
				<SidebarControls />
			</div>

			{lastTimeSaved && (
				<p className="text-default-500 mt-0 w-full truncate px-1 text-center text-xs lg:mt-2">
					<span>{isSubmitted ? 'Submitted on: ' : 'Last saved: '}</span>
					{DateTime.fromMillis(lastTimeSaved).toFormat('LLL d, yyyy')}
					{' at '}
					{DateTime.fromMillis(lastTimeSaved).toLocaleString(DateTime.TIME_SIMPLE)}
					{' ' + DateTime.fromMillis(lastTimeSaved).offsetNameShort}
				</p>
			)}
		</aside>
	)
}
